import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  EmptyState,
  InfiniteScrollSentinel,
  ListSearch,
  ListSummary,
} from '@/components/ui/cifras/list-toolbar';
import {
  InitialsAvatar,
  ResourceCard,
  ResourceCardGrid,
} from '@/components/ui/cifras/resource-card';
import { Content } from '@/components/ui/content';
import { ActionsButton } from '@/components/ui/data-table';
import { Icon } from '@/components/ui/icon';
import { Segmented } from '@/components/ui/segmented';
import { userRoleOptions } from '@/db/schema/users';
import { useRouter } from '@/hooks/use-router';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { isAdminTierRole } from '@/lib/permissions';
import { useIsAuthenticated, usePermissions } from '@/services/auth';
import { useDeleteUser, useInfiniteUsers, userKeys } from '@/services/users';
import { ResetPasswordButton } from './+components/reset-password-button';

export const Route = createFileRoute('/_app/users/')({
  head: () => meta({ label: 'Usuários' }),
  component: RouteComponent,
});

type RoleFilter = 'all' | 'admin' | 'user';

const model = 'users';
const PER_PAGE = 24;

const roleLabel = (role: string) =>
  userRoleOptions.find((option) => option.value === role)?.label ?? role;

function RouteComponent() {
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { user: currentUser } = useIsAuthenticated();
  const { canCreate, canUpdate, canDelete } = createPermissions(model);
  const { mutateAsync: remove } = useDeleteUser();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<RoleFilter>('all');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteUsers({
    search: search || undefined,
    role: role === 'all' ? undefined : role,
    orderBy: 'name',
    order: 'asc',
    perPage: PER_PAGE,
  });

  const users = data?.pages.flatMap((page) => page.data) ?? [];
  const total = data?.pages[0]?.pagination.total ?? 0;

  return (
    <Content
      breadcrumbs={{
        icon: 'users',
        title: 'Usuários',
        action: canCreate && (
          <Button variant="success" onClick={() => push('/users/create')} icon="plus">
            Adicionar
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <ListSearch
            className="min-w-[260px] flex-1"
            placeholder="Buscar por nome ou e-mail..."
            onSearch={setSearch}
          />
          <Segmented<RoleFilter>
            value={role}
            onChange={setRole}
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'admin', label: 'Administradores' },
              { value: 'user', label: 'Usuários' },
            ]}
          />
        </div>

        {data && <ListSummary shown={users.length} total={total} noun="usuários" />}

        {data && users.length === 0 ? (
          <EmptyState
            title="Nenhum usuário encontrado"
            hint={search || role !== 'all' ? 'Tente outro filtro ou termo.' : undefined}
          />
        ) : (
          <ResourceCardGrid min={320}>
            {users.map((user) => {
              // Ninguém edita, exclui ou reseta a própria senha por aqui (vai pelo Meu Perfil).
              const isSelf = user.id === currentUser?.id;
              const canEditThis = canUpdate && !isSelf;

              return (
                <ResourceCard
                  key={user.id}
                  muted={user.isActive === false}
                  leading={<InitialsAvatar name={user.name} />}
                  title={user.name}
                  subtitle={user.email}
                  trailing={
                    // Inativo continua listado (só fica barrado no login).
                    user.isActive === false ? (
                      <Badge variant="destructive">Inativo</Badge>
                    ) : undefined
                  }
                  meta={
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="shield" className="size-3.5 text-accent-ink" />
                        {roleLabel(user.role)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="clock" className="size-3.5 text-accent-ink" />
                        {date(user.createdAt as unknown as string).format('DD/MM/YYYY')}
                      </span>
                    </>
                  }
                  actions={
                    <>
                      {isAdminTierRole(currentUser?.role) && !isSelf && (
                        <ResetPasswordButton userId={user.id} />
                      )}
                      <ActionsButton
                        id={user.id}
                        edit={canEditThis}
                        remove={canDelete && !isSelf}
                        onEdit={(id) => push(`/users/${id}`)}
                        onRemove={(id) => remove({ id })}
                        queryKey={userKeys.all}
                      />
                    </>
                  }
                  onClick={canEditThis ? () => push(`/users/${user.id}`) : undefined}
                />
              );
            })}
          </ResourceCardGrid>
        )}

        <InfiniteScrollSentinel
          hasNextPage={Boolean(hasNextPage)}
          isFetching={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </div>
    </Content>
  );
}
